import { motion } from 'framer-motion';
import { Heart, Globe, Users, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Family First',
      description: 'Every experience we curate is designed with families in mind, ensuring adventures that everyone can enjoy together.',
      color: '#F28A30'
    },
    {
      icon: <Globe className="w-10 h-10" />,
      title: 'Authentic Stories',
      description: 'We share real experiences from real travels, offering genuine insights you won\'t find in guidebooks.',
      color: '#1E6FBA'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Community Driven',
      description: 'From walking tours to local partnerships, we believe in connecting people and celebrating our community.',
      color: '#2E8B57'
    },
    {
      icon: <MapPin className="w-10 h-10" />,
      title: 'Local Expertise',
      description: 'Based in Penticton, we know the Okanagan inside and out, from hidden gems to must-see attractions.',
      color: '#F28A30'
    }
  ];

  return (
    <div className="w-full" data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/1157386/pexels-photo-1157386.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              About AnReal Travel
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Turning trips into unforgettable stories, one adventure at a time
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="story-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our Story
          </h2>
          <div className="space-y-6 text-lg text-[#4A4A4A] leading-relaxed">
            <p>
              AnReal Travel was born from a simple belief: travel should be accessible, authentic, and unforgettable. Based in beautiful Penticton, BC, we're a family-focused travel brand passionate about both exploring the world and celebrating everything our local community has to offer.
            </p>
            <p>
              From the sun-soaked beaches of the Okanagan to the vibrant streets of Tokyo, we believe every destination has stories worth sharing. Our mission is to inspire families and travelers to explore with confidence, armed with real experiences and insider tips that transform ordinary trips into extraordinary adventures.
            </p>
            <p>
              Whether you're planning a weekend in wine country or dreaming of far-off destinations, we're here to make your journey easier, richer, and more meaningful.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Values */}
      <section className="bg-[#F5E9D4] grain-texture py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              What We Stand For
            </h2>
            <p className="text-[#4A4A4A] text-lg">
              The values that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-sm"
                data-testid={`value-${index}`}
              >
                <div style={{ color: value.color }} className="mb-4">
                  {value.icon}
                </div>
                <h3 className="text-2xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {value.title}
                </h3>
                <p className="text-[#4A4A4A] leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="offerings-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            What We Offer
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="bg-[#1E6FBA] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              1
            </div>
            <h3 className="text-xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Penticton Experiences
            </h3>
            <p className="text-[#4A4A4A]">
              Guided walking tours, local itineraries, and insider tips for exploring our beautiful home.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-center"
          >
            <div className="bg-[#2E8B57] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              2
            </div>
            <h3 className="text-xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Global Travel Stories
            </h3>
            <p className="text-[#4A4A4A]">
              Real experiences from destinations around the world, with practical tips and authentic insights.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <div className="bg-[#F28A30] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              3
            </div>
            <h3 className="text-xl mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Custom Trip Planning
            </h3>
            <p className="text-[#4A4A4A]">
              Personalized itineraries for Okanagan visits and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1E6FBA] py-16" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Create Your Next Story?
            </h2>
            <Link to="/contact">
              <Button
                size="lg"
                className="btn-scale bg-white text-[#1E6FBA] hover:bg-gray-100 rounded-full px-8 py-6"
                data-testid="cta-get-in-touch"
              >
                Get In Touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
